import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion'
import { isNil } from 'lodash'

import { Button } from '@/components/ui'
import { cn } from '@/utils'

import { cardVariants } from '../constants'

type TMetricCard = {
  title: string
  icon: React.ElementType
  value?: number
  unit?: string
  isDecimalValue?: boolean
  isDateValue?: boolean
  isFullSizeUnit?: boolean
  isLoading?: boolean
  onViewAll?: () => void
}

const MetricCard: React.FC<TMetricCard> = (props) => {
  const { t } = useTranslation()
  const {
    title,
    icon: Icon,
    value,
    unit = '',
    isDecimalValue = false,
    isDateValue = false,
    isFullSizeUnit = false,
    isLoading = false,
    onViewAll,
  } = props

  const ref = React.useRef(null)

  const motionValue = useMotionValue(0)
  // useSpring creates a smooth, physics-based animation to the target
  const springValue = useSpring(motionValue, {
    damping: 100,
    stiffness: 1000,
  })
  // useTransform formats the number with locale string formatting, preserving decimals
  const formattedValue = useTransform(springValue, (v) => {
    return v.toLocaleString('en-US', {
      maximumFractionDigits: isDecimalValue ? 2 : 0,
      minimumFractionDigits: 0,
    })
  })

  React.useEffect(() => {
    if (!isNil(value)) {
      // Animate the motion value to the target number when in view
      motionValue.set(value)
    }
  }, [motionValue, value])

  return (
    <motion.div
      variants={cardVariants}
      className="flex-1 rounded-xl p-2xl border border-solid border-secondary bg-primary shadow-sm overflow-hidden min-w-72"
    >
      <div className="bg-tertiary rounded-full size-12 flex items-center justify-center">
        <Icon size={24} className="size-6 text-primary-900" />
      </div>
      <div className="mt-5 w-full text-md text-tertiary-600 font-semibold">
        {title}
      </div>
      <div className="mt-2 w-full flex justify-between items-end flex-wrap gap-2">
        <div
          className={cn(
            'text-4xl font-semibold text-primary-900 tracking-[-2%]',
            isFullSizeUnit && !isDateValue ? '' : 'space-x-1.5'
          )}
        >
          <motion.span ref={ref}>{formattedValue}</motion.span>
          {unit && (
            <span
              className={cn(
                isFullSizeUnit
                  ? 'font-semibold text-primary-900'
                  : 'text-sm text-secondary-700 font-medium'
              )}
            >
              {unit}
            </span>
          )}
        </div>
        {onViewAll ? (
          <Button
            variant="text"
            color="default"
            className={cn(
              'bg-transparent border-none p-0 m-0 text-sm font-semibold text-brand-tertiary-600 cursor-pointer rounded-none ml-auto',
              isLoading && 'cursor-not-allowed opacity-50'
            )}
            disabled={isLoading}
            loading={isLoading}
            onClick={isLoading ? undefined : onViewAll}
          >
            {t('dashboard.actions.viewAll')}
          </Button>
        ) : null}
      </div>
    </motion.div>
  )
}

const MemoizedMetricCard = React.memo(MetricCard)
MemoizedMetricCard.displayName = 'MetricCard'

export { MemoizedMetricCard as MetricCard }
